import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { postdatas } from "../../apis/apiService";
import { useSelector } from "react-redux";

export default function useTaskTimer() {
  const [timers, setTimers] = useState({});
  const [activeTimerId, setActiveTimerId] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const timerRef = useRef(null);
  const login_user = useSelector((state) => state.selectOptions?.loginUserName);
  const { id: login_user_id } = login_user || {};

  // Rehydrate state from localStorage on load
  useEffect(() => {
    const savedTimers = localStorage.getItem("timers");
    const parsed = savedTimers ? JSON.parse(savedTimers) : {};

    for (const [id, timer] of Object.entries(parsed)) {
      if (timer.startTime && !timer.paused) {
        const diffInSeconds = dayjs().diff(dayjs(timer.startTime), "second");
        const hours = Math.floor(diffInSeconds / 3600);
        const minutes = Math.floor((diffInSeconds % 3600) / 60);
        const seconds = diffInSeconds % 60;

        parsed[id] = {
          ...timer,
          hours,
          minutes,
          seconds,
        };

        setActiveTimerId(id);
        setIsPaused(false);
      }
    }

    setTimers(parsed);
  }, []);

  const startTimer = (row) => {
    const { id } = row;
    const now = new Date().toISOString();

    setCurrentRow(row);
    setIsPaused(false);
    setActiveTimerId(id);

    setTimers((prev) => {
      const existing = prev[id] || {
        hours: 0,
        minutes: 0,
        seconds: 0,
      };
      const updated = {
        ...prev,
        [id]: {
          ...existing,
          startTime: existing.startTime || now,
          paused: false,
        },
      };
      localStorage.setItem("timers", JSON.stringify(updated));
      return updated;
    });
  };

  const pauseTimer = () => {
    setIsPaused(true);
    setTimers((prev) => {
      const updated = {
        ...prev,
        [activeTimerId]: {
          ...prev[activeTimerId],
          paused: true,
        },
      };
      localStorage.setItem("timers", JSON.stringify(updated));
      return updated;
    });
  };

  const resumeTimer = () => {
    setIsPaused(false);
    setTimers((prev) => {
      const updated = {
        ...prev,
        [activeTimerId]: {
          ...prev[activeTimerId],
          paused: false,
        },
      };
      localStorage.setItem("timers", JSON.stringify(updated));
      return updated;
    });
  };

  const cancelTimer = () => {
    if (activeTimerId) {
      clearInterval(timerRef.current);
      const resetTimers = {
        ...timers,
        [activeTimerId]: {
          hours: 0,
          minutes: 0,
          seconds: 0,
          startTime: null,
          paused: false,
        },
      };
      setTimers(resetTimers);
      localStorage.setItem("timers", JSON.stringify(resetTimers));
    }

    setActiveTimerId(null);
    setIsPaused(false);
    setCurrentRow(null);
  };

  const stopTimer = async () => {
    clearInterval(timerRef.current);

    if (currentRow && timers[activeTimerId]) {
      const now = new Date();
      const timerData = timers[activeTimerId];
      const startTimeRaw = timerData.startTime
        ? new Date(timerData.startTime)
        : now;
      const endTimeRaw = now;

      const formattedStartTime = dayjs(startTimeRaw).format("HH:mm");
      const formattedEndTime = dayjs(endTimeRaw).format("HH:mm");
      const displayStartTime = dayjs(startTimeRaw).format("hh:mm A");
      const displayEndTime = dayjs(endTimeRaw).format("hh:mm A");
      const today = dayjs().format("YYYY-MM-DD");

      const { isConfirmed, value: note } = await Swal.fire({
        title: "Timer Details",
        html: `
          <p><strong>Start Time:</strong> ${displayStartTime}</p>
          <p><strong>End Time:</strong> ${displayEndTime}</p>
          <p><strong>Date:</strong> ${today}</p>
          <textarea id="noteInput" class="swal2-textarea" placeholder="Enter note"></textarea>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "OK",
        cancelButtonText: "Cancel",
        preConfirm: async () => {
          const noteValue = document.getElementById("noteInput").value;
          if (!noteValue) {
            Swal.showValidationMessage("Note is required");
            return;
          }

          const payload = {
            start_time: formattedStartTime,
            end_time: formattedEndTime,
            log_date: today,
            task_id: currentRow.id,
            notes: noteValue,
            billing_type: currentRow.billing_type,
            project_id: currentRow.project_id,
            owner_id: login_user_id,
          };

          try {
            const response = await postdatas("timesheet", "add", payload);
            await Swal.fire({
              icon: "success",
              title: "Success",
              text: response.message,
            });
            return noteValue;
          } catch (error) {
            await Swal.fire({
              icon: "error",
              title: "Error",
              text: error.message || "Something went wrong!",
            });
            return false;
          }
        },
      });

      if (isConfirmed || note === false) {
        const resetTimers = {
          ...timers,
          [activeTimerId]: {
            hours: 0,
            minutes: 0,
            seconds: 0,
            startTime: null,
            paused: false,
          },
        };
        setTimers(resetTimers);
        localStorage.setItem("timers", JSON.stringify(resetTimers));
      }

      setActiveTimerId(null);
      setIsPaused(false);
      setCurrentRow(null);
    }
  };

  // Timer loop to increase time (for display purposes only)
  useEffect(() => {
    if (!activeTimerId) return;

    clearInterval(timerRef.current);

    if (!isPaused) {
      timerRef.current = setInterval(() => {
        setTimers((prev) => {
          const current = prev[activeTimerId];
          if (!current) return prev;

          let { hours, minutes, seconds } = current;
          seconds += 1;
          if (seconds === 60) {
            seconds = 0;
            minutes += 1;
          }
          if (minutes === 60) {
            minutes = 0;
            hours += 1;
          }

          const updated = {
            ...prev,
            [activeTimerId]: {
              ...current,
              hours,
              minutes,
              seconds,
            },
          };
          localStorage.setItem("timers", JSON.stringify(updated));
          return updated;
        });
      }, 1000);
    }

    return () => clearInterval(timerRef.current);
  }, [isPaused, activeTimerId]);

  return {
    timers,
    activeTimerId,
    currentRow, 
    isPaused,
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,      
    cancelTimer,
  };
}
